import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { EvaluationFormStore, EvaluationFormData } from "./types";

const initialFormData: EvaluationFormData = {
  propertyType: "",
  subPropertyType: "",
  location: "",
  rooms: "",
  constructionYear: "",
  renovationYear: "",
  livingSpace: "",
  plotSize: "",
  floors: "",
  efficiency: "",
  balconySize: "",
  garageSpaces: "",
  parkingSpaces: "",
  condition: "",
  equipment: "",
  reason: "",
  timeline: "",
  basement: "",
  commercialUnits: "",
  commercialArea: "",
  quality: "",
  first_name: "",
  last_name: "",
  street: "",
  city: "",
  country: "",
  email: "",
  home_phone: "",
  accept_contact: false,
  newsletter: false,
  description: "",
  house_number: "",
  zip_code: "",
};

const useEvaluationFormStore = create<EvaluationFormStore>()(
  devtools(
    persist(
      immer((set) => ({
        // State
        formData: initialFormData,
        step: 1,
        started: false,
        completedSteps: [],

        // Actions
        updateFormData: (payload) =>
          set((state) => {
            const updates =
              typeof payload === "function" ? payload(state.formData) : payload;
            state.formData = { ...state.formData, ...updates };
          }),

        setStep: (newStep) =>
          set((state) => {
            state.step = newStep;
          }),

        setStarted: (started) =>
          set((state) => {
            state.started = started;
          }),

        addCompletedStep: (stepId) =>
          set((state) => {
            if (!state.completedSteps.includes(stepId)) {
              state.completedSteps.push(stepId);
            }
          }),

        resetForm: () =>
          set((state) => {
            state.formData = { ...initialFormData };
            state.step = 1;
            state.started = false;
            state.completedSteps = [];
          }),
      })),
      {
        name: "evaluation-form-storage",
        partialize: (state) => ({
          formData: state.formData,
          completedSteps: state.completedSteps,
          step: state.step,
          started: state.started,
        }),
      }
    ),
    { name: "EvaluationFormStore" }
  )
);

// Selectors for optimized re-renders
export const useFormData = () =>
  useEvaluationFormStore((state) => state.formData);
export const useFormStep = () => useEvaluationFormStore((state) => state.step);
export const useFormStarted = () =>
  useEvaluationFormStore((state) => state.started);
export const useCompletedSteps = () =>
  useEvaluationFormStore((state) => state.completedSteps);

// Individual action selectors to avoid object recreation
export const useUpdateFormData = () =>
  useEvaluationFormStore((state) => state.updateFormData);
export const useSetStep = () =>
  useEvaluationFormStore((state) => state.setStep);
export const useSetStarted = () =>
  useEvaluationFormStore((state) => state.setStarted);
export const useAddCompletedStep = () =>
  useEvaluationFormStore((state) => state.addCompletedStep);
export const useResetForm = () =>
  useEvaluationFormStore((state) => state.resetForm);

// Combined actions hook (for backward compatibility, but prefer individual hooks)
export const useFormActions = () => {
  const updateFormData = useUpdateFormData();
  const setStep = useSetStep();
  const setStarted = useSetStarted();
  const addCompletedStep = useAddCompletedStep();
  const resetForm = useResetForm();
  return { updateFormData, setStep, setStarted, addCompletedStep, resetForm };
};

export const useLastCompletedStepId = () =>
  useEvaluationFormStore((state) => {
    if (state.completedSteps.length === 0) return undefined;
    return state.completedSteps[state.completedSteps.length - 1];
  });

export default useEvaluationFormStore;
